import {
	Hash,
	User,
	Wrench,
	Zap,
	Calendar,
	FileText,
	MapPin,
	Clock,
} from "lucide-react";

type InfoPanelProps = {
	requestId?: string;
	clientName?: string;
	categoryName?: string;
	urgency?: string;
	description?: string;
	date?: string;
	hour?: string;
	location?: string;
};

const normalizeRequestDescription = (raw?: string) => {
	if (!raw) return "";
	const trimmed = raw.trim();
	if (!trimmed) return "";

	const marker = "SERVIX_REQUEST_V1::";
	if (!trimmed.startsWith(marker)) return trimmed;

	const encodedPayload = trimmed.slice(marker.length).trim();
	if (!encodedPayload) return "";

	try {
		const parsed = JSON.parse(encodedPayload) as {
			description?: string;
			items?: Array<{ name?: string }>;
		};

		const parsedDescription = parsed.description?.trim();
		if (parsedDescription) return parsedDescription;

		const names = (parsed.items ?? [])
			.map((item) => item.name?.trim())
			.filter((name): name is string => Boolean(name));
		if (names.length > 0) return `servicio ${names.join(", ")}`;

		return trimmed;
	} catch {
		return trimmed;
	}
};

export function InfoPanel({
	requestId = "-",
	clientName = "Cliente",
	urgency = "Media",
	description,
	date,
	hour,
	location,
}: InfoPanelProps) {
	const safeDescription = normalizeRequestDescription(description);

	const rows = [
		{ label: "ID Solicitud", icon: Hash, value: requestId },
		{ label: "Cliente", icon: User, value: clientName },
		...(location ? [{ label: "Ubicación", icon: MapPin, value: location }] : []),
	];

	const urgencyColorMap: Record<string, { bg: string; text: string }> = {
		Alta: { bg: "bg-red-100", text: "text-red-700" },
		Media: { bg: "bg-amber-100", text: "text-amber-700" },
		Baja: { bg: "bg-green-100", text: "text-green-700" },
	};
	const urgencyStyle = urgencyColorMap[urgency] ?? {
		bg: "bg-slate-100",
		text: "text-slate-700",
	};

	return (
		<aside className='lg:col-span-1'>
			<div className='rounded-2xl p-2 sm:p-3'>
				<h3 className='mb-4 flex items-center gap-2 text-base font-semibold text-text-primary'>
					<FileText className='h-5 w-5 text-primary' />
					Información de Solicitud
				</h3>
				<div className='space-y-4 text-sm sm:space-y-5 sm:text-[15px]'>
					{rows.map(({ label, icon: Icon, value }) => (
						<div key={label} className='space-y-1'>
							<div className='flex items-center gap-2 text-sm font-semibold text-text-tertiary'>
								<Icon className='h-4 w-4' />
								<span>{label}</span>
							</div>
							<div className='wrap-break-word text-text-secondary'>{value}</div>
						</div>
					))}

					<div className='space-y-1'>
						<div className='flex items-center gap-2 text-sm font-semibold text-text-tertiary'>
							<Zap className='h-4 w-4' />
							<span>Urgencia</span>
						</div>
						<span
							className={`inline-flex w-fit items-center rounded-full px-3 py-1 text-sm font-semibold ${urgencyStyle.bg} ${urgencyStyle.text}`}
						>
							{urgency}
						</span>
					</div>

					{safeDescription && (
						<div className='space-y-1'>
							<div className='flex items-center gap-2 text-sm font-semibold text-text-tertiary'>
								<Wrench className='h-4 w-4' />
								<span>Descripción</span>
							</div>
							<p className='wrap-break-word text-text-secondary'>{safeDescription}</p>
						</div>
					)}

					{date && (
						<div className='space-y-1'>
							<div className='flex items-center gap-2 text-sm font-semibold text-text-tertiary'>
								<Calendar className='h-4 w-4' />
								<span>Fecha preferida</span>
							</div>
							<div className='text-text-secondary'>{date}</div>
						</div>
					)}

					{hour && (
						<div className='space-y-1'>
							<div className='flex items-center gap-2 text-sm font-semibold text-text-tertiary'>
								<Clock className='h-4 w-4' />
								<span>Hora preferida</span>
							</div>
							<div className='text-text-secondary'>{hour}</div>
						</div>
					)}
				</div>
			</div>
		</aside>
	);
}
